import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, AfterViewInit, SimpleChanges, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DocumentViewerComponent } from '../../../../global/components/common/document-viewer/modal-image.component';
import { organisationConfigurationSelector, languageSelector } from '../../../../global/store/global.selectors';
import { State } from '../../../../store';
import { Request, RequestMessage } from '../../../models/request.model';
import { RequestsV2ImplService } from '../../../services/requests-v2.impl.service';

interface PendingAttachment {
    url: string;
    type: 'image' | 'pdf' | 'other';
    name?: string;
}

@Component({
    selector: 'app-request-messages',
    templateUrl: './request-messages.component.html',
    styleUrls: ['./request-messages.component.scss'],
})
export class RequestMessagesComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @ViewChild('messagesContainer') messagesContainer: ElementRef;

    @Input() request: Request;
    @Input() canEdit = false;

    @Input() isPolling = false;

    @Output() messagesSent = new EventEmitter<void>();

    @Output() messagesRead = new EventEmitter<void>();

    messageForm: FormGroup;
    sending = false;
    uploading = false;
    pendingAttachments: PendingAttachment[] = [];
    currentAdminId: string;
    initialUnreadCount: number | null = null;
    unreadDividerIndex: number | null = null;
    predefinedMessages: { id: string; text: string }[] = [];
    private destroy$ = new Subject<void>();
    private previousMessageCount = 0;
    private pendingUploadCount = 0;

    constructor(
        private readonly fb: FormBuilder,
        private readonly requestsService: RequestsV2ImplService,
        private readonly translateService: TranslateService,
        private readonly dialog: MatDialog,
        private readonly store: Store<State>,
    ) {}

    ngOnInit(): void {
        this.initForm();
        this.loadPredefinedMessages();
    }

    ngAfterViewInit(): void {
        // Initial scroll to bottom after view is ready
        setTimeout(() => this.scrollToBottom(), 100);
    }

    ngOnChanges(changes: SimpleChanges): void {
        const requestIdChanged = !!changes.request && !changes.request.firstChange && changes.request.previousValue?._id !== changes.request.currentValue?._id;
        if (requestIdChanged) {
            this.initialUnreadCount = null;
            this.unreadDividerIndex = null;
            this.previousMessageCount = changes.request.currentValue?.messages?.length || 0;
        }

        // Capture initial unread count and the divider's index before it gets reset to 0.
        // The index is fixed at the position it started at, so messages appended afterwards
        // (replies sent, new messages polled in) don't push the divider along with them.
        if ((changes.request?.firstChange || requestIdChanged) && this.initialUnreadCount === null) {
            this.initialUnreadCount = changes.request.currentValue?.adminUnreadMessageCount || 0;
            const initialMessageCount = changes.request.currentValue?.messages?.length || 0;
            const dividerIndex = this.initialUnreadCount > 0 ? initialMessageCount - this.initialUnreadCount : null;
            this.unreadDividerIndex = dividerIndex !== null ? Math.max(0, dividerIndex) : null;
        }

        // Clear the divider once messages have actually been marked as read
        if (changes.request && !changes.request.firstChange && changes.request.currentValue?.adminUnreadMessageCount === 0 && (this.initialUnreadCount ?? 0) > 0) {
            this.initialUnreadCount = 0;
            this.unreadDividerIndex = null;
        }

        // Scroll to bottom when messages change (new message received)
        if (changes.request && !changes.request.firstChange) {
            const currentMessages = changes.request.currentValue?.messages?.length || 0;
            const previousMessages = this.previousMessageCount;

            if (currentMessages > previousMessages) {
                // New message detected, scroll to bottom
                setTimeout(() => this.scrollToBottom(), 100);
            }
            this.previousMessageCount = currentMessages;
        } else if (changes.request?.firstChange) {
            this.previousMessageCount = changes.request.currentValue?.messages?.length || 0;
        }
    }

    // Prevents *ngFor from tearing down/recreating unchanged messages (and resetting scroll) on every poll
    trackByMessageId(_index: number, message: RequestMessage): string {
        return message._id;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initForm(): void {
        this.messageForm = this.fb.group({
            message: ['', [Validators.required]],
        });
    }

    private loadPredefinedMessages(): void {
        let currentLang: string;
        this.store
            .select(languageSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((lang) => {
                currentLang = lang;
            });

        this.store
            .select(organisationConfigurationSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((config) => {
                const lang = currentLang || this.translateService.currentLang || 'fr';
                const messages = config?.request?.predefinedMessages || [];
                this.predefinedMessages = messages.map((m) => ({ id: m.id, text: m[lang] || '' })).filter((m) => m.text);
            });
    }

    selectPredefinedMessage(text: string): void {
        this.messageForm.get('message')?.setValue(text);
    }

    /**
     * Get messages sorted by date (using sentAt)
     */
    get sortedMessages(): RequestMessage[] {
        if (!this.request?.messages) {
            return [];
        }
        return [...this.request.messages].sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());
    }

    /**
     * Get attachment URLs for sending to API
     */
    get attachmentUrls(): string[] {
        return this.pendingAttachments.map((a) => a.url);
    }

    /**
     * Check if message is from admin
     */
    isAdminMessage(message: RequestMessage): boolean {
        return message?.isAdmin === true;
    }

    /**
     * Get user initials for avatar
     */
    getInitials(firstName: string, lastName: string): string {
        const first = firstName?.charAt(0)?.toUpperCase() || '';
        const last = lastName?.charAt(0)?.toUpperCase() || '';
        return `${first}${last}` || 'A';
    }

    /**
     * Get the index where unread messages start
     */
    getUnreadDividerIndex(): number {
        // For now, return -1 (no divider) since we mark as read when tab opens
        // This could be enhanced to track the last read message
        return -1;
    }

    /**
     * Format date for message timestamp (using sentAt)
     */
    formatMessageDate(date: string | Date): string {
        const msgDate = new Date(date);
        if (Number.isNaN(msgDate.getTime())) {
            return '';
        }
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (msgDate.toDateString() === today.toDateString()) {
            return this.translateService.instant('request_v2.history.time.today');
        }
        if (msgDate.toDateString() === yesterday.toDateString()) {
            return this.translateService.instant('request_v2.history.time.one_day_ago');
        }

        const locale = this.translateService.currentLang === 'fr' ? 'fr-CA' : 'en-CA';
        return msgDate.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
    }

    /**
     * Format time for message
     */
    formatMessageTime(date: string | Date): string {
        const msgDate = new Date(date);
        if (Number.isNaN(msgDate.getTime())) {
            return '';
        }
        return msgDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    /**
     * Get valid attachments (filter out empty strings)
     */
    getValidAttachments(attachments: string[] | undefined): string[] {
        if (!attachments) {
            return [];
        }
        return attachments.filter((a) => a && a.trim() !== '');
    }

    /**
     * Detect file type from URL or filename
     */
    private getFileType(url: string, fileName?: string): 'image' | 'pdf' | 'other' {
        const lowerUrl = (fileName || url).toLowerCase();
        if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(lowerUrl)) {
            return 'image';
        }
        if (/\.pdf$/i.test(lowerUrl)) {
            return 'pdf';
        }
        return 'other';
    }

    /**
     * Check if URL is an image
     */
    isImage(url: string): boolean {
        return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i.test(url.toLowerCase());
    }

    /**
     * Check if URL is a PDF
     */
    isPdf(url: string): boolean {
        return /\.pdf(\?|$)/i.test(url.toLowerCase());
    }

    /**
     * Check if file is deleted
     */
    isFileDeleted(attachment: string): boolean {
        return attachment === 'FILE_DELETED';
    }

    /**
     * Send new message
     */
    sendMessage(): void {
        if (this.messageForm.invalid || this.sending) {
            return;
        }

        const messageText = this.messageForm.get('message')?.value?.trim();
        if (!messageText) {
            return;
        }

        this.sending = true;

        this.requestsService.sendMessage(this.request._id, messageText, this.attachmentUrls.length > 0 ? this.attachmentUrls : undefined).subscribe({
            next: () => {
                this.messageForm.reset();
                this.pendingAttachments = [];
                this.sending = false;
                this.messagesSent.emit();
                setTimeout(() => this.scrollToBottom(), 100);
            },
            error: () => {
                this.sending = false;
            },
        });
    }

    /**
     * Handle native file input selection
     */
    onNativeFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files?.length) {
            return;
        }

        Array.from(input.files).forEach((file) => {
            this.uploadFile(file);
        });

        // Reset input so same file can be selected again
        input.value = '';
    }

    /**
     * Upload a single file and add to attachments
     */
    private uploadFile(file: File): void {
        const fileType = this.getFileType(file.name, file.name);

        // Track upload state
        this.pendingUploadCount += 1;
        this.uploading = true;

        this.requestsService.uploadFile(file).subscribe({
            next: (uploadedUrls: string[]) => {
                if (uploadedUrls?.length > 0) {
                    uploadedUrls.forEach((url) => {
                        this.pendingAttachments.push({
                            url,
                            type: fileType,
                            name: file.name,
                        });
                    });
                }
                this.decrementUploadCount();
            },
            error: () => {
                this.decrementUploadCount();
            },
        });
    }

    /**
     * Decrement upload count and update uploading state
     */
    private decrementUploadCount(): void {
        this.pendingUploadCount -= 1;
        if (this.pendingUploadCount <= 0) {
            this.pendingUploadCount = 0;
            this.uploading = false;
        }
    }

    /**
     * Remove attachment
     */
    removeAttachment(index: number): void {
        this.pendingAttachments.splice(index, 1);
    }

    /**
     * Scroll messages to bottom with smooth animation
     */
    private scrollToBottom(): void {
        if (this.messagesContainer?.nativeElement) {
            const element = this.messagesContainer.nativeElement;
            element.scrollTo({
                top: element.scrollHeight,
                behavior: 'smooth',
            });
        }
    }

    /**
     * Open document viewer for an attachment
     */
    openDocumentViewer(url: string): void {
        this.dialog.open(DocumentViewerComponent, {
            maxWidth: '100vw',
            maxHeight: '100vh',
            data: { src: url },
            panelClass: 'image-dialog',
        });
    }
}
